import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { getApiUrl } from '../utils/apiUrl';
import { DEMO_MODE, demoTeams } from '../services/demoData';

const NotificationsWidget = ({ onClick }) => {
  const [, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const getToken = () => localStorage.getItem('token');
  const API_URL = getApiUrl();

  // Hent brukerens teams
  const fetchMyTeams = async () => {
    // Check for demo mode
    if (DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true') {
      return demoTeams;
    }
    
    try {
      const response = await fetch(`${API_URL}/teams/my-teams`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
    return [];
  };

  // Hent varsler (kun de 3 nyeste uleste)
  const fetchNotifications = async (teams) => {
    // Check for demo mode
    if (DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true') {
      setNotifications([
        { id: 1, message: 'Demo: Ny arbeidsordre AO-2024-001', type: 'info', created_at: new Date().toISOString(), teamName: 'VANNSKJÆRING' },
        { id: 2, message: 'Demo: Status oppdatert', type: 'success', created_at: new Date(Date.now() - 3600000).toISOString(), teamName: 'SVEIS' },
      ]);
      return;
    }
    
    try {
      const allNotifications = [];
      
      for (const team of teams) {
        const response = await fetch(
          `${API_URL}/notifications/team/${team.id}?unreadOnly=true`,
          {
            headers: {
              'Authorization': `Bearer ${getToken()}`
            }
          }
        );

        if (response.ok) {
          const teamNotifications = await response.json();
          allNotifications.push(...teamNotifications.map(n => ({
            ...n,
            teamName: team.name
          })));
        }
      }

      // Sorter etter dato og ta kun de 3 nyeste
      allNotifications.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotifications(allNotifications.slice(0, 3));
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Hent antall uleste
  const fetchUnreadCount = async () => {
    // Check for demo mode
    if (DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true') {
      setUnreadCount(2);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/notifications/unread-count`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  // Last inn data
  const loadData = async () => {
    const teams = await fetchMyTeams();
    if (teams.length > 0) {
      await fetchNotifications(teams);
      await fetchUnreadCount();
    }
  };

  useEffect(() => {
    loadData();
    // Oppdater hver 30. sekund
    const interval = setInterval(() => {
      loadData();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Ikon basert på type
  // eslint-disable-next-line no-unused-vars
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'warning':
      case 'error':
        return <WarningIcon fontSize="small" color="warning" />;
      default:
        return <InfoIcon fontSize="small" color="info" />;
    }
  };

  // Formater tidspunkt
  // eslint-disable-next-line no-unused-vars
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Ukjent';
    
    const date = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return 'Ukjent';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Nå';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}t`;
    return date.toLocaleDateString('nb-NO');
  };

  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        height: '100%',
        minHeight: 100,
        background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
        color: 'white',
        boxShadow: 3,
        '&:active': {
          transform: 'scale(0.98)',
        },
        transition: 'transform 0.1s',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <NotificationsIcon sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Varslinger
          </Typography>
          {unreadCount > 0 && (
            <Box sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              color: 'white',
              borderRadius: '10px',
              padding: '0.15rem 0.5rem',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              minWidth: '20px',
              textAlign: 'center',
              ml: 1
            }}>
              {unreadCount}
            </Box>
          )}
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Se alle varslinger og oppdateringer
        </Typography>
      </CardContent>
    </Card>
  );
};

export default NotificationsWidget;
